/**
 * Global Store Utilities
 *
 * Helper functions to interact with Redux store exposed globally.
 * Available only in development mode for debugging purposes.
 */

import type { AppDispatch, RootState } from './store'

/**
 * Get the global Redux store instance
 * @returns Redux store instance or undefined if not available
 */
export const getGlobalStore = () => {
  if (typeof window !== 'undefined' && window.__REDUX_STORE__) {
    return window.__REDUX_STORE__
  }
  console.warn(
    'Global Redux store is not available. Make sure you are in development mode.',
  )
  return undefined
}

/**
 * Get the global Redux dispatch function
 * @returns Redux dispatch function or undefined if not available
 */
export const getGlobalDispatch = (): AppDispatch | undefined => {
  if (typeof window !== 'undefined' && window.__REDUX_DISPATCH__) {
    return window.__REDUX_DISPATCH__
  }
  console.warn(
    'Global Redux dispatch is not available. Make sure you are in development mode.',
  )
  return undefined
}

/**
 * Get current Redux state
 * @returns Current state or undefined if store not available
 */
export const getGlobalState = (): RootState | undefined => {
  const store = getGlobalStore()
  return store?.getState()
}

/**
 * Subscribe to store changes
 * @param callback Function to call on state changes
 * @returns Unsubscribe function or undefined if store not available
 */
export const subscribeToGlobalStore = (callback: () => void) => {
  const store = getGlobalStore()
  return store?.subscribe(callback)
}

/**
 * Helper function to log current state to console
 */
export const logCurrentState = () => {
  const state = getGlobalState()
  if (state) {
    console.groupEnd()
  }
}

/**
 * Helper function to dispatch an action and log the result
 * @param action Redux action to dispatch
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const dispatchAndLog = (action: any) => {
  const dispatch = getGlobalDispatch()
  if (dispatch) {
    console.log('🚀 Dispatching action:', action)
    dispatch(action)
    setTimeout(() => logCurrentState(), 0) // Log state after dispatch
  }
}

// =============================================================================
// FLUTTER INTEGRATION - Redux Actions Bridge
// =============================================================================

import type { AuthLoginStep, IAuthorizedUser } from 'auth/models'
import {
  clearUser,
  clearUserApplicationid,
  clearUserClientId,
  clearUserSiteId,
  setInitialAuthState,
  setIsLoginPageUiCenter,
  setLoginStep,
  setUser,
} from 'auth/store'
import type { IMenuItem } from 'common/models'
import {
  clearMenu,
  clearRouting,
  closeMobileMenu,
  closeMoreMenu,
  disableLoader,
  enableLoader,
  openMobileMenu,
  openMoreMenu,
  setIframeUrl,
  setInitialIframeState,
  setIsTokenChanged,
  setLoader,
  setLogo,
  setLogoColor,
  setMenu,
  setPageTitle,
  setRouting,
  toggleMobileMenu,
  toggleMoreMenu,
} from 'iframe/store'

/**
 * Global Redux Actions Bridge for Flutter Integration
 *
 * These functions are exposed globally to allow Flutter WebView
 * to interact with Redux store actions.
 */
export const ReduxActionsBridge = {
  // Core
  dispatchRaw: (type: string, payload?: unknown) => {
    const dispatch = getGlobalDispatch()
    if (dispatch) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch({ type, payload } as any)
      return true
    }
    return false
  },

  // Auth Actions
  auth: {
    setLoginStep: (step: AuthLoginStep) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setLoginStep({ step }))
        return true
      }
      return false
    },

    setUser: (user: IAuthorizedUser) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setUser({ user }))
        return true
      }
      return false
    },

    clearUser: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(clearUser())
        return true
      }
      return false
    },

    clearUserClientId: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(clearUserClientId())
        return true
      }
      return false
    },

    clearUserApplicationId: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(clearUserApplicationid())
        return true
      }
      return false
    },

    clearUserSiteId: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(clearUserSiteId())
        return true
      }
      return false
    },

    setIsLoginPageUiCenter: (isCenter: boolean) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setIsLoginPageUiCenter({ isLoginPageUiCenter: isCenter }))
        return true
      }
      return false
    },

    resetAuthState: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setInitialAuthState())
        return true
      }
      return false
    },
  },

  // Iframe Actions
  iframe: {
    setMenu: (menu: IMenuItem[]) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setMenu(menu))
        return true
      }
      return false
    },

    clearMenu: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(clearMenu())
        return true
      }
      return false
    },

    setRouting: (routing: string[]) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setRouting(routing))
        return true
      }
      return false
    },

    clearRouting: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(clearRouting())
        return true
      }
      return false
    },

    setLogo: (logo: string, isImage: boolean = true) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setLogo({ logo, isImage }))
        return true
      }
      return false
    },

    setLogoColor: (logoColor: string, isImageColor: boolean = true) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setLogoColor({ logoColor, isImageColor }))
        return true
      }
      return false
    },

    setIframeUrl: (url: string) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setIframeUrl(url))
        return true
      }
      return false
    },

    setIsTokenChanged: (isChanged: boolean) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setIsTokenChanged(isChanged))
        return true
      }
      return false
    },

    setPageTitle: (title: string) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setPageTitle(title))
        return true
      }
      return false
    },

    setLoader: (isLoading: boolean) => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setLoader(isLoading))
        return true
      }
      return false
    },

    enableLoader: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(enableLoader())
        return true
      }
      return false
    },

    disableLoader: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(disableLoader())
        return true
      }
      return false
    },

    resetIframeState: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(setInitialIframeState())
        return true
      }
      return false
    },

    openMobileMenu: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(openMobileMenu())
        return true
      }
      return false
    },

    closeMobileMenu: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(closeMobileMenu())
        return true
      }
      return false
    },

    toggleMobileMenu: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(toggleMobileMenu())
        return true
      }
      return false
    },

    openMoreMenu: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(openMoreMenu())
        return true
      }
      return false
    },

    closeMoreMenu: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(closeMoreMenu())
        return true
      }
      return false
    },

    toggleMoreMenu: () => {
      const dispatch = getGlobalDispatch()
      if (dispatch) {
        dispatch(toggleMoreMenu())
        return true
      }
      return false
    },
  },
}
